import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Building2, CheckCircle, AlertCircle, ExternalLink, Settings, Loader2 } from "lucide-react"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import axios from 'axios'

// Types for QuickBooks API responses
interface QuickBooksConnectionStatus {
  isConnected: boolean
  realmId?: string
  connectedDate?: string
  isEnabled: boolean
}

interface QuickBooksAuthResponse {
  authorizationUrl: string
  state: string
}

interface QuickBooksTestResponse {
  success: boolean
  message: string
  realmId?: string
}

export const Route = createFileRoute('/_app/settings/integrations/quickbooks')({
  component: QuickBooksIntegrationPage,
})

function QuickBooksIntegrationPage() {
  const [connectionStatus, setConnectionStatus] = useState<QuickBooksConnectionStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [testResult, setTestResult] = useState<QuickBooksTestResponse | null>(null)

  useEffect(() => {
    fetchConnectionStatus()
  }, [])

  const fetchConnectionStatus = async () => {
    try {
      const response = await axios.get('/integrations/quickbooks/status')

      if (response.status === 200) {
        setConnectionStatus(response.data)
      } else {
        console.error('Failed to fetch connection status')
      }
    } catch (error) {
      console.error('Error fetching connection status:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleConnect = async () => {
    setIsConnecting(true)
    try {
      const response = await axios.post('/integrations/quickbooks/connect', {});

      if (response.status === 200) {
        const authResponse: QuickBooksAuthResponse = response.data
        // Redirect to QuickBooks OAuth
        window.location.href = authResponse.authorizationUrl
      } else {
        console.error('Failed to initiate QuickBooks connection')
      }
    } catch (error) {
      console.error('Error connecting to QuickBooks:', error)
    } finally {
      setIsConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    setIsDisconnecting(true)
    try {
      const response = await axios.post('/integrations/quickbooks/disconnect', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}` // Adjust based on your auth setup
        }
      })

      if (response.status === 200) {
        await fetchConnectionStatus() // Refresh status
      } else {
        console.error('Failed to disconnect QuickBooks')
      }
    } catch (error) {
      console.error('Error disconnecting QuickBooks:', error)
    } finally {
      setIsDisconnecting(false)
    }
  }

  const handleTestConnection = async () => {
    setIsTesting(true)
    try {
      const response = await axios.post('/integrations/quickbooks/test', {})

      if (response.status === 200) {
        const result: QuickBooksTestResponse = response.data
        setTestResult(result)
      } else {
        setTestResult({ success: false, message: 'Failed to test connection' })
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Error testing connection' })
    } finally {
      setIsTesting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="h-full w-full p-6 bg-gray-100 overflow-auto">
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading QuickBooks integration status...</span>
        </div>
      </div>
    )
  }

  const isConnected = connectionStatus?.isConnected ?? false

  return (
    <>
      <header
        className={`sticky top-0 flex shrink-0 items-center gap-2 p-2 bg-white border-b-4 border-l-gray-200 border-r-gray-200 border-t-gray-200 border-t-2`}
      >
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink asChild>
                <Link to='/settings'>
                  Settings
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink asChild>
                <Link to='/settings/integrations'>
                  Integrations
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>
                QuickBooks Online
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </header>

      <div className="h-full w-full p-6 bg-gray-100 overflow-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">QuickBooks Online Integration</h1>
              <p className="text-muted-foreground">
                Sync your contribution data with QuickBooks Online
              </p>
            </div>
          </div>

          {/* Connection Status */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Connection Status
                    {isConnected ? (
                      <Badge variant="secondary" className="text-green-700 bg-green-100">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-600">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Not Connected
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    {isConnected
                      ? "CoolFocus is successfully connected to your QuickBooks Online account"
                      : "Connect CoolFocus to your QuickBooks Online account to start syncing contribution data"
                    }
                  </CardDescription>
                </div>
                <Button
                  className="flex items-center gap-2"
                  onClick={isConnected ? handleDisconnect : handleConnect}
                  disabled={isConnecting || isDisconnecting}
                >
                  {isConnecting || isDisconnecting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isConnected ? (
                    <>
                      <Settings className="h-4 w-4" />
                      Disconnect
                    </>
                  ) : (
                    <>
                      <ExternalLink className="h-4 w-4" />
                      Connect to QuickBooks
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            {isConnected && (
              <CardContent>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    onClick={handleTestConnection}
                    disabled={isTesting}
                  >
                    {isTesting ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Test Connection
                  </Button>
                  {testResult && (
                    <Alert className={testResult.success ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
                      <AlertCircle className={`h-4 w-4 ${testResult.success ? "text-green-600" : "text-red-600"}`} />
                      <AlertDescription className={testResult.success ? "text-green-700" : "text-red-700"}>
                        {testResult.message}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            )}
          </Card>

          {/* What Gets Synced */}
          <Card>
            <CardHeader>
              <CardTitle>What Gets Synced</CardTitle>
              <CardDescription>
                Here's what CoolFocus will sync with your QuickBooks Online account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3 p-4 border rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium">Contribution Records</h4>
                  <p className="text-sm text-muted-foreground">
                    All donations and contributions are sent to QuickBooks Online as income transactions.
                    This helps you track your organization's revenue and maintain accurate financial records.
                  </p>
                </div>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Note:</strong> Only contribution data is synced to QuickBooks Online.
                  Donor personal information and other CoolFocus data remains secure and is not shared.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* How It Works */}
          <Card>
            <CardHeader>
              <CardTitle>How It Works</CardTitle>
              <CardDescription>
                Understanding the QuickBooks Online integration process
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-2">1</div>
                  <h4 className="font-medium mb-2">Connect Your Account</h4>
                  <p className="text-sm text-muted-foreground">
                    Securely authorize CoolFocus to access your QuickBooks Online account
                  </p>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-2">2</div>
                  <h4 className="font-medium mb-2">Automatic Sync</h4>
                  <p className="text-sm text-muted-foreground">
                    Contributions are automatically sent to QuickBooks when they're processed in CoolFocus
                  </p>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-2">3</div>
                  <h4 className="font-medium mb-2">Stay in Sync</h4>
                  <p className="text-sm text-muted-foreground">
                    Your financial records stay up-to-date across both platforms automatically
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Features & Benefits */}
          <Card>
            <CardHeader>
              <CardTitle>Features & Benefits</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium">Real-time Synchronization</h4>
                      <p className="text-sm text-muted-foreground">
                        Contributions appear in QuickBooks immediately after processing
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium">Accurate Financial Records</h4>
                      <p className="text-sm text-muted-foreground">
                        Maintain precise donation tracking for tax and reporting purposes
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium">Time Savings</h4>
                      <p className="text-sm text-muted-foreground">
                        Eliminate manual data entry between systems
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium">Secure & Reliable</h4>
                      <p className="text-sm text-muted-foreground">
                        Bank-level security with automatic error handling
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Getting Started */}
          {!isConnected && (
            <Card>
              <CardHeader>
                <CardTitle>Ready to Get Started?</CardTitle>
                <CardDescription>
                  Connect your QuickBooks Online account in just a few clicks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <Button
                    size="lg"
                    className="flex items-center gap-2"
                    onClick={handleConnect}
                    disabled={isConnecting}
                  >
                    {isConnecting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ExternalLink className="h-4 w-4" />
                    )}
                    Connect QuickBooks Online
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    You'll be redirected to QuickBooks to authorize the connection
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  )
}
